import { Skeleton, Card, Table } from 'antd';

/**
 * Table skeleton loader
 */
export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  const columns = [
    { title: '', dataIndex: 'col1', key: 'col1' },
    { title: '', dataIndex: 'col2', key: 'col2' },
    { title: '', dataIndex: 'col3', key: 'col3' },
    { title: '', dataIndex: 'col4', key: 'col4' },
    { title: '', dataIndex: 'col5', key: 'col5' },
  ];

  const dataSource = Array.from({ length: rows }, (_, index) => ({
    key: index,
    col1: <Skeleton.Input active size="small" style={{ width: 120 }} />,
    col2: <Skeleton.Input active size="small" style={{ width: 150 }} />,
    col3: <Skeleton.Input active size="small" style={{ width: 100 }} />,
    col4: <Skeleton.Input active size="small" style={{ width: 130 }} />,
    col5: <Skeleton.Input active size="small" style={{ width: 80 }} />,
  }));

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      showHeader={false}
    />
  );
};

/**
 * Card skeleton loader
 */
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} style={{ marginBottom: 16 }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </Card>
      ))}
    </>
  );
};

/**
 * Stats card skeleton loader
 */
export const StatsCardSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {Array.from({ length: count }).map((_, index) => (
        <Card key={index} style={{ flex: '1 1 200px', minWidth: 200 }}>
          <Skeleton.Avatar active size="large" shape="circle" />
          <div style={{ marginTop: 16 }}>
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        </Card>
      ))}
    </div>
  );
};

/**
 * Form skeleton loader
 */
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 5 }) => {
  return (
    <div style={{ maxWidth: 600 }}>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} style={{ marginBottom: 24 }}>
          <Skeleton.Input
            active
            size="small"
            style={{ width: 120, marginBottom: 8 }}
          />
          <Skeleton.Input active block style={{ height: 40 }} />
        </div>
      ))}
    </div>
  );
};

/**
 * Profile skeleton loader
 */
export const ProfileSkeleton: React.FC = () => {
  return (
    <div style={{ display: 'flex', gap: 24 }}>
      <Skeleton.Avatar active size={100} shape="circle" />
      <div style={{ flex: 1 }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </div>
    </div>
  );
};

/**
 * List skeleton loader
 */
export const ListSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {Array.from({ length: items }).map((_, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: 16,
            border: '1px solid #f0f0f0',
            borderRadius: 8,
          }}
        >
          <Skeleton.Avatar active size="default" shape="circle" />
          <div style={{ flex: 1 }}>
            <Skeleton active paragraph={{ rows: 1 }} />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Dashboard skeleton loader
 */
export const DashboardSkeleton: React.FC = () => {
  return (
    <div>
      <Skeleton.Input active style={{ width: 200, height: 32, marginBottom: 24 }} />
      
      {/* Period Filter Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Skeleton.Input active size="small" style={{ width: 120 }} />
          <Skeleton.Input active size="large" style={{ width: 200 }} />
        </div>
      </Card>

      {/* Stats Cards Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index}>
            <Skeleton.Input active size="small" style={{ width: 100, marginBottom: 12 }} />
            <Skeleton.Input active size="large" style={{ width: 150 }} />
          </Card>
        ))}
      </div>

      {/* Charts Row - 16/8 split */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card>
          <Skeleton.Input active size="small" style={{ width: 200, marginBottom: 16 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
        <Card>
          <Skeleton.Input active size="small" style={{ width: 150, marginBottom: 16 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>

      {/* Products Table */}
      <Card>
        <Skeleton.Input active size="small" style={{ width: 200, marginBottom: 16 }} />
        <TableSkeleton rows={5} />
      </Card>
    </div>
  );
};

/**
 * Member card skeleton
 */
export const MemberCardSkeleton: React.FC = () => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16 }}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Skeleton.Avatar active size="default" />
            <div style={{ flex: 1 }}>
              <Skeleton.Input active size="small" style={{ width: '100%', marginBottom: 8 }} />
              <Skeleton.Input active size="small" style={{ width: '60%' }} />
            </div>
          </div>
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      ))}
    </div>
  );
};
